import { Flex, Headline, Loader } from '@bamboohr/fabric';
import { useCallback, useEffect, useRef, useState } from 'react';
import { ChatLogs } from 'src/components/ChatLogs';
import SideBar from 'src/components/sideBar/SideBar';
import UserAvatar from 'src/components/UserAvatar';
import { useLlmStore, usePromptStore } from 'src/store';
import {
	AnswerResponse,
	Chat,
	LlmConfig,
	SupplementalResult,
	SupplementUpdate,
} from 'src/types/global';
import NewChatContainer from '../../components/NewChatContainer';
import config from '../../config';
import api, { supplementApi } from '../../services/api';
import { newRequestId, requestIdHeaders } from '../../utils/requestId';
import { reportError } from '../../utils/rollbar';
import { ChatLog, Supplement } from './home.types';
import {
	buildChatRequest,
	buildSupplementRequest,
	getChatSessions,
	hasCustomLLMConfig,
	parseSupplementObservation,
	supplementsFromStoredResults,
} from './utils';
import { useMessageStore } from 'src/store';
import { variantData } from 'src/components/messageBanner/variantData';

// Configs
const CHAT_ENDPOINT = config.VERSION_ENDPOINT + 'chat';
const ANSWER_ENDPOINT = config.VERSION_ENDPOINT + 'answer';
const CHAT_SESSIONS_ENDPOINT = config.VERSION_ENDPOINT + 'chat-session';
const GENERATE_NAME_ENDPOINT =
	config.VERSION_ENDPOINT + 'chat-session/generate-name';
const INITIAL_SESSION_LIMIT = 30;
const SESSION_INCREMENT_LIMIT = 10;
const NO_SUPPLEMENTAL_FUNCTION_IDS: string[] = [];

const Home = ({ currentUser, setCurrentUser }) => {
	// Chat Info & Data
	const [currentChatSessionId, setCurrentChatSessionId] = useState('');
	const [currentChatSessionName, setCurrentChatSessionName] = useState('');
	const [chatSessionNames, setChatSessionNames] = useState<Chat[]>([]);
	const [isLoadingMoreSessions, setIsLoadingMoreSessions] = useState(false);
	const [sessionCursor, setSessionCursor] = useState<string | null>(null);
	const [chatLog, setChatLog] = useState<ChatLog[]>([]);
	const [isChatloading, setIsChatLoading] = useState<boolean>(false);
	const [hasPrinted, setHasPrinted] = useState(false);

	// Input & Feedback State
	const [responseFromAPI, setReponseFromAPI] = useState(false);
	const [errorOccurred, setErrorOccurred] = useState(false);

	const [, setModes] = useState({});

	const showMessage = useMessageStore((state) => state.setMessage);

	// Refs
	const chatLogRef = useRef<HTMLDivElement>(null);

	const prompt = usePromptStore((state) => state.prompt);
	const updatePrompt = usePromptStore((state) => state.updatePrompt);
	const clearPrompt = usePromptStore((state) => state.clearPrompt);
	const llmId = useLlmStore((state) => state.llmId);
	const llmLabel = useLlmStore((state) => state.llmLabel);
	const llmConfig = useLlmStore((state) => state.llmConfig);
	const setLlmId = useLlmStore((state) => state.updateLlmId);
	const setLlmLabel = useLlmStore((state) => state.updateLlmLabel);
	const updateLlmState = useLlmStore((state) => state.updateLlmState);
	// Selected raw rather than defaulted inside the selector: a fresh [] would be
	// a new reference on every call and re-render on every unrelated store write.
	const supplementalFunctionIds =
		useLlmStore((state) => state.supplementalFunctionIdsByMode[state.llmId]) ??
		NO_SUPPLEMENTAL_FUNCTION_IDS;

	const displayErrorBanner = (status: string) => {
		const { headerText, subText } =
			variantData[status] || variantData['generic-error'];

		showMessage({
			message: headerText,
			description: subText,
			visible: true,
			type: 'error',
		});
	};

	useEffect(() => {
		async function fetchData() {
			const { chats, sessionCursor } = await getChatSessions(
				CHAT_SESSIONS_ENDPOINT,
				INITIAL_SESSION_LIMIT
			);
			setChatSessionNames(chats);
			setSessionCursor(sessionCursor);
		}
		fetchData();
	}, []);

	const removeMostRecentPrompt = useCallback(() => {
		setChatLog((prev) => prev.slice(0, -1));
	}, []);

	useEffect(() => {
		if (errorOccurred) {
			removeMostRecentPrompt();
			setErrorOccurred(false);
			if (chatLog.length === 1) {
				api.delete(`${CHAT_SESSIONS_ENDPOINT}/${currentChatSessionId}`);
			}
		}
	}, [chatLog, currentChatSessionId, errorOccurred, removeMostRecentPrompt]);

	useEffect(() => {
		if (!currentChatSessionId) return;

		const thisChat = chatSessionNames.find(
			(x) => x.id === currentChatSessionId,
		);

		if (thisChat) {
			setLlmId(thisChat.llmId || config.DEFAULT_FUNCTION_ID);
			setLlmLabel(thisChat.llmLabel || config.DEFAULT_FUNCTION_LABEL);
		}
	}, [currentChatSessionId, chatSessionNames, setLlmId, setLlmLabel]);

	useEffect(() => {
		setTimeout(() => {
			scrollToBottom();
		}, 400);
	}, [chatLog]);

	const mergeSupplement = (
		requestId: string,
		functionId: string,
		settled: Partial<Omit<Supplement, 'functionId'>>,
	) => {
		setChatLog((prev) =>
			prev.map((row) =>
				row.requestId === requestId
					? {
							...row,
							supplements: (row.supplements ?? []).map((supplement) =>
								supplement.functionId === functionId
									? { ...supplement, ...settled }
									: supplement,
							),
						}
					: row,
			),
		);
	};

	const failSupplement = (
		requestId: string,
		functionId: string,
		message: string,
		error: Error,
	) => {
		mergeSupplement(requestId, functionId, { status: 'error' });
		reportError(message, error, { requestId });
		return { function_id: functionId, result: 'failure' as const };
	};

	const putSupplementResult = (
		chatId: string,
		entry: SupplementUpdate,
		requestId: string,
	) => {
		supplementApi
			.put(
				`${CHAT_ENDPOINT}/${chatId}`,
				{ ...entry, return_supplemental_result: true },
				{ headers: requestIdHeaders(requestId) },
			)
			.then(({ data }: { data: SupplementalResult | string }) => {
				// Saved, but the API did not return the entry.
				if (data === config.SUPPLEMENT_UPDATE_SUCCESS_MESSAGE) return;
				if (
					typeof data !== 'object' ||
					data?.function_id !== entry.function_id
				) {
					reportError(
						`Supplement ${entry.function_id}: result was not appended to the chat`,
						new Error(String(data)),
						{ requestId },
					);
					return;
				}
				if (typeof data.shared_with_customer === 'boolean') {
					mergeSupplement(requestId, entry.function_id, {
						sharedWithCustomer: data.shared_with_customer,
					});
				}
			})
			.catch((error) => {
				reportError(`Supplement ${entry.function_id}: ${error.message}`, error, {
					requestId,
				});
			});
	};

	/**
	 * `priorChatLog` must exclude this turn's optimistic row: its answer has not
	 * arrived, and the API rejects a message with no content.
	 */
	const requestSupplements = (
		requestId: string,
		inputPrompt: string,
		priorChatLog: ChatLog[],
	): Promise<SupplementUpdate[]> => {
		const messages = buildSupplementRequest(priorChatLog);

		return Promise.all(
			supplementalFunctionIds.map((functionId) =>
				supplementApi
					.post(
						ANSWER_ENDPOINT,
						{
							function_id: functionId,
							params: { context: { prompt: inputPrompt, messages } },
						},
						{ headers: requestIdHeaders(requestId) },
					)
					.then(({ data }: { data: AnswerResponse }) => {
						if (data?.result === 'failure') {
							return failSupplement(
								requestId,
								functionId,
								`Supplement ${functionId}: answer returned a failure result`,
								new Error('Supplemental answer failed'),
							);
						}

						const parsed = parseSupplementObservation(data?.observation);
						if (parsed.status === 'error') {
							return failSupplement(
								requestId,
								functionId,
								`Supplement ${functionId}: response did not match the answer contract`,
								new Error('Supplemental observation could not be parsed'),
							);
						}

						mergeSupplement(requestId, functionId, {
							status: parsed.status,
							text: parsed.text,
						});
						return {
							function_id: functionId,
							result: 'success' as const,
							// Rendered in full above; only what is stored is capped.
							observation: parsed.text?.slice(
								0,
								config.SUPPLEMENT_OBSERVATION_MAX_LENGTH,
							),
							abstained: parsed.abstained,
							usage: data?.usage,
							'elapsed-time': data?.['elapsed-time'],
							'llm-config': data?.['llm-config'],
						};
					})
					.catch((error) =>
						failSupplement(
							requestId,
							functionId,
							`Supplement ${functionId}: ${error.message}`,
							error,
						),
					),
			),
		);
	};

	const callAPI = async (
		inputPrompt: string,
		requestId: string,
		supplements: Promise<SupplementUpdate[]> | null,
	) => {
		const headers = requestIdHeaders(requestId);
		try {
			let response;
			if (chatLog.length > 0 && currentChatSessionId) {
				const request = buildRequest(currentChatSessionId, inputPrompt);
				response = await api.post(CHAT_ENDPOINT, request, { headers });
			} else {
				// Create new Chat Session document
				const newChatSession = await api.post(
					CHAT_SESSIONS_ENDPOINT,
					undefined,
					{ headers },
				);
				setCurrentChatSessionId(newChatSession.data.id);

				// Make new Chat document
				const request = buildRequest(newChatSession.data.id, inputPrompt);
				response = await api.post(CHAT_ENDPOINT, request, { headers });
				const status = response.data.status;
				if (status === 'llm-service-error') {
					throw new Error('LLM service provider unavailable');
				}
				const sessionName = await api.post(
					GENERATE_NAME_ENDPOINT,
					{
						question: inputPrompt,
						answer: response.data.observation,
					},
					{ headers },
				);
				setChatSessionNames((prev) => [
					...prev,
					{
						name: sessionName.data,
						id: newChatSession.data.id,
						llmId,
						llmLabel,
						llmConfig,
						time_created: new Date(newChatSession.data.time_created),
					},
				]);
				setCurrentChatSessionName(sessionName.data);
				await api.put(
					`${CHAT_SESSIONS_ENDPOINT}/${newChatSession.data.id}`,
					{
						session_name: sessionName.data,
						function_id: llmId,
						llm_config: llmConfig,
						llm_label: llmLabel,
					},
					{ headers },
				);
			}
			const data = response.data;
			setChatLog((prev) => [
				...prev.slice(0, -1), // Remove the last prompt to avoid duplication
				{
					...prev[prev.length - 1],
					chatPrompt: inputPrompt,
					botMessage: data.observation,
					id: data.id,
					llmId,
					llmLabel,
					rating: data.rating,
					unhelpful: data.unhelpful,
					incorrect: data.incorrect,
					harmful: data.harmful,
					comment: data.comment,
					sources: data.sources,
				},
			]);
			// Closed over rather than read back from chatLog at PUT time, which would
			// lose the entry if the user switches sessions before it settles.
			const chatId = data.id;
			if (supplements && chatId) {
				supplements.then((entries) =>
					entries.forEach((entry) =>
						putSupplementResult(chatId, entry, requestId),
					),
				);
			}
			if (data.status && data.status !== 'token-limit-warning') {
				throw new Error(data.status);
			}
		} catch (error) {
			updatePrompt(inputPrompt);
			console.error(error);
			const status =
				error.status || error?.response?.data?.status || 'generic-error';

			if (error.response?.status === 401) {
				displayErrorBanner('cookies-expired');
			} else {
				displayErrorBanner(status);
			}

			setErrorOccurred(true);
			reportError(error.message, error, { requestId });
		}
		setReponseFromAPI(false);
		setIsChatLoading(false); // Ensure the loader is hidden after the API call
	};

	const handleSubmit = async (
		textAreaTarget: HTMLTextAreaElement | HTMLInputElement,
	) => {
		setHasPrinted(false);

		if (!llmId) return;

		const userPrompt = prompt;
		clearPrompt();
		textAreaTarget.style.height = 'auto';

		if (!responseFromAPI && userPrompt.trim() !== '') {
			setReponseFromAPI(true);
			const requestId = newRequestId();
			const hasSupplements = supplementalFunctionIds.length > 0;
			// Appended before the fan-out so the merge is queued behind it and always
			// finds the row.
			setChatLog((prev) => [
				...prev,
				{
					chatPrompt: userPrompt,
					requestId,
					...(hasSupplements
						? {
								supplements: supplementalFunctionIds.map((functionId) => ({
									functionId,
									status: 'loading' as const,
								})),
							}
						: {}),
				},
			]);
			const supplements = hasSupplements
				? requestSupplements(requestId, userPrompt, chatLog)
				: null;
			textAreaTarget.blur();
			await callAPI(userPrompt, requestId, supplements);

			if (prompt !== '') {
				textAreaTarget.style.height = `${textAreaTarget.scrollHeight}px`;
			}
			textAreaTarget.focus();
		}
	};

	const buildRequest = (chatSessionId: string, inputPrompt: string) => {
		const context = { prompt: inputPrompt };
		const params = { context };
		context['messages'] = buildChatRequest(chatLog);

		if (hasCustomLLMConfig(config, llmId)) {
			params['llm-config'] = llmConfig;
		}

		return { function_id: llmId, 'chat-session-id': chatSessionId, params };
	};

	const scrollToBottom = () => {
		if (chatLogRef.current) {
			const isUserAtBottom =
				chatLogRef.current.scrollHeight - chatLogRef.current.scrollTop ===
				chatLogRef.current.clientHeight;
			if (isUserAtBottom) {
				chatLogRef.current?.scrollIntoView({
					behavior: 'smooth',
					block: 'end',
				});
			}
		}
	};

	const logout = async () => {
		setChatLog([]);
		setChatSessionNames([]);
		setCurrentUser(null);
	};

	const createNewChat = () => {
		setChatLog([]);
		setCurrentChatSessionId('');
		setCurrentChatSessionName('');
		updateLlmState({
			newId: config.DEFAULT_FUNCTION_ID,
			newLabel: config.DEFAULT_FUNCTION_LABEL,
			newConfig: config.DEFAULT_LLM_CONFIG,
		});
	};

	const clearAllChats = () => {
		setChatLog([]);
		setCurrentChatSessionId('');
		setCurrentChatSessionName('');
		setChatSessionNames([]);
		setSessionCursor(null);
		updateLlmState({
			newId: config.DEFAULT_FUNCTION_ID,
			newLabel: config.DEFAULT_FUNCTION_LABEL,
			newConfig: config.DEFAULT_LLM_CONFIG,
		});
	};

	const loadChatLogs = async (
		chatID: string,
		chatName: string,
		label: string,
		id: string,
		newConfig: LlmConfig,
	) => {
		setHasPrinted(true);
		setIsChatLoading(true);
		try {
			const res = await api.get(`${CHAT_ENDPOINT}/${chatID}`);
			const savedChatLog = res.data.map((resp) => ({
				chatPrompt: resp['prompt'],
				botMessage: resp['response'],
				id: resp.id,
				rating: resp['rating'],
				unhelpful: resp['unhelpful'],
				incorrect: resp['incorrect'],
				harmful: resp['harmful'],
				comment: resp['comment'],
				sources: resp['sources'],
				supplements: supplementsFromStoredResults(resp['supplemental_results']),
			}));
			setChatLog(savedChatLog);
			setCurrentChatSessionId(chatID);
			setCurrentChatSessionName(chatName);
			updateLlmState({
				newId: id,
				newLabel: label,
				newConfig,
			});
			clearPrompt();
			setIsChatLoading(false);
		} catch (error) {
			const status =
				error.status || error?.response?.data?.status || 'generic-error';
			displayErrorBanner(status);
		}
	};

	const handleLoadMoreSessions = async () => {
		if (isLoadingMoreSessions || !sessionCursor) {
			return;
		}
		setIsLoadingMoreSessions(true);
		try {
			const { chats: moreSessions, sessionCursor: newCursor } = await getChatSessions(
				CHAT_SESSIONS_ENDPOINT,
				SESSION_INCREMENT_LIMIT,
				sessionCursor,
			);
			setChatSessionNames((prev) => [...prev, ...moreSessions]);
			setSessionCursor(newCursor);
		} finally {
			setTimeout(() => {
				setIsLoadingMoreSessions(false)
			}, 500);
		}
	};

	return (
		<Flex height="100%" width="100vw" padding={'24px'}>
			<SideBar
				createNewChat={createNewChat}
				loadChatLogs={loadChatLogs}
				chatNames={chatSessionNames}
				setChatNames={setChatSessionNames}
				setChatName={setCurrentChatSessionName}
				activeId={currentChatSessionId}
				hasMoreSessions={!!sessionCursor}
				isLoadingMoreSessions={isLoadingMoreSessions}
				handleLoadMoreSessions={handleLoadMoreSessions}
			/>
			<Flex
				flexDirection={'column'}
				width="100%"
				height="100%"
				gap={2}
				minWidth={0}
			>
				<Flex alignItems="center" justifyContent={'space-between'}>
					<span className="chatSessionNameContainer">
						<Headline size="medium">{currentChatSessionName}</Headline>
					</span>
					<UserAvatar
						currentUser={currentUser}
						logout={logout}
						clearAllChats={clearAllChats}
					/>
				</Flex>
				<div className="chatContainer">
					{isChatloading ? (
						<Flex flex={1} alignItems="center" justifyContent="center">
							<Loader />
						</Flex>
					) : (
						<>
							{chatLog.length > 0 ? (
								<ChatLogs
									chatLogRef={chatLogRef}
									chatLog={chatLog}
									handleSubmit={handleSubmit}
									hasPrinted={hasPrinted}
									setHasPrinted={setHasPrinted}
								/>
							) : (
								<>
									<NewChatContainer
										currentUser={currentUser}
										handleSubmit={handleSubmit}
										setModes={setModes}
									/>
								</>
							)}
						</>
					)}
				</div>
			</Flex>
		</Flex>
	);
};

export default Home;
